import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  BarChart as StatisticsIcon,
  ChevronRight as ExpandIcon,
  ChevronLeft as CollapseIcon,
  InsightsTwoTone as InsightsIcon,
  Tv as TvIcon,
  LocalShipping as IMPSIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

const SideNav = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };

  const mainMenuItems = [
    { path: '/', label: 'Dashboard', icon: DashboardIcon },
    { path: '/analytics', label: 'Analytics', icon: TrendingUpIcon },
    { path: '/reports', label: 'Reports', icon: AssessmentIcon },
    { path: '/statistics', label: 'Statistics', icon: StatisticsIcon },
    { path: '/advanced', label: 'Advanced', icon: InsightsIcon },
  ];

  const analyticsMenuItems = [
    {
      key: 'atrack',
      label: 'ATRACK Analytics',
      icon: TaskIcon,
      path: '/analytics/atrack'
    },
    {
      key: 'ipms',
      label: 'IPMS Analytics',
      icon: IMPSIcon,
      path: '/analytics/ipms'
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {!desktopCollapsed && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#667eea',
              fontSize: '1rem',
              letterSpacing: '-0.5px',
              m: 0
            }}
          >
            Graviences
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 0, display: desktopCollapsed ? 'none' : 'block' }} />

      {/* Menu Items */}
      <Box sx={{ flex: 1, overflow: 'auto', p: desktopCollapsed ? 0.5 : 1 }}>
        <List sx={{ p: 0, m: 0 }}>
          {/* Main Menu Items */}
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={item.path}
                title={desktopCollapsed ? item.label : ''}
                placement="right"
              >
                <ListItem
                  button
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 1,
                    px: desktopCollapsed ? 1 : 2,
                    py: 1.5,
                    bgcolor: isActive ? '#667eea' : 'transparent',
                    color: isActive ? '#ffffff' : '#6b7280',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                    '&:hover': {
                      bgcolor: isActive ? '#667eea' : '#f3f4f6',
                      color: isActive ? '#ffffff' : '#667eea',
                      transform: 'translateX(4px)'
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: 0,
                      justifyContent: 'center',
                      marginRight: desktopCollapsed ? 0 : 12
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      fontSize: '1.5rem'
                    }}
                  >
                    <Icon />
                  </ListItemIcon>
                  {!desktopCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: isActive ? 600 : 500,
                        letterSpacing: '-0.3px'
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}

          {/* Divider */}
          {!desktopCollapsed && <Divider sx={{ my: 2 }} />}

          {/* Analytics Menu Items */}
          {analyticsMenuItems.map((menuItem) => {
            const Icon = menuItem.icon;
            const isActive = location.pathname === menuItem.path;
            return (
              <Tooltip
                key={menuItem.path}
                title={desktopCollapsed ? menuItem.label : ''}
                placement="right"
              >
                <ListItem
                  button
                  onClick={() => handleNavigate(menuItem.path)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 1,
                    px: desktopCollapsed ? 1 : 2,
                    py: 1.5,
                    bgcolor: isActive ? '#667eea' : 'transparent',
                    color: isActive ? '#ffffff' : '#6b7280',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                    '&:hover': {
                      bgcolor: isActive ? '#667eea' : '#f3f4f6',
                      color: isActive ? '#ffffff' : '#667eea',
                      transform: 'translateX(4px)'
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: 0,
                      justifyContent: 'center',
                      marginRight: desktopCollapsed ? 0 : 12
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      fontSize: '1.5rem'
                    }}
                  >
                    <Icon />
                  </ListItemIcon>
                  {!desktopCollapsed && (
                    <ListItemText
                      primary={menuItem.label}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: isActive ? 600 : 500,
                        letterSpacing: '-0.3px'
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}

          {/* TV Mode - at the bottom */}
          <Divider sx={{ my: 2, display: desktopCollapsed ? 'none' : 'block' }} />
          <Tooltip title={desktopCollapsed ? 'TV Mode' : ''} placement="right">
            <ListItem
              button
              onClick={() => handleNavigate('/tv')}
              sx={{
                borderRadius: 1.5,
                mb: 1,
                px: desktopCollapsed ? 1 : 2,
                py: 1.5,
                bgcolor: location.pathname === '/tv' ? '#667eea' : 'transparent',
                color: location.pathname === '/tv' ? '#ffffff' : '#6b7280',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                '&:hover': {
                  bgcolor: location.pathname === '/tv' ? '#667eea' : '#f3f4f6',
                  color: location.pathname === '/tv' ? '#ffffff' : '#667eea',
                  transform: 'translateX(4px)'
                },
                '& .MuiListItemIcon-root': {
                  minWidth: 0,
                  justifyContent: 'center',
                  marginRight: desktopCollapsed ? 0 : 12
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', fontSize: '1.5rem' }}>
                <TvIcon />
              </ListItemIcon>
              {!desktopCollapsed && (
                <ListItemText
                  primary="TV Mode"
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === '/tv' ? 600 : 500,
                    letterSpacing: '-0.3px'
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        </List>
      </Box>

      {/* Footer Toggle (Desktop Only) */}
      {!isMobile && (
        <Box sx={{ p: 0.5, borderTop: '1px solid #e5e7eb' }}>
          <IconButton
            onClick={handleDesktopToggle}
            sx={{
              width: '100%',
              color: '#9ca3af',
              '&:hover': {
                bgcolor: '#f3f4f6',
                color: '#667eea'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {desktopCollapsed ? <ExpandIcon /> : <CollapseIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  const currentDrawerWidth = desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#ffffff',
          color: '#1f2937',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                color: '#667eea',
                '&:hover': {
                  bgcolor: '#f3f4f6'
                }
              }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#667eea',
              flex: 1,
              fontSize: '1.25rem',
              letterSpacing: '-0.5px'
            }}
          >
            Graviences Analytics
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer - Permanent */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: currentDrawerWidth,
            flexShrink: 0,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiDrawer-paper': {
              width: currentDrawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid #e5e7eb',
              mt: '64px',
              height: 'calc(100% - 64px)',
              bgcolor: '#ffffff',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile Drawer - Temporary */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              bgcolor: '#ffffff'
            }
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: '#f9fafb',
            p: 0,
            px: { xs: 0.5, sm: 1 },
            py: 1
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SideNav;
